<template lang="pug">
#send-message-setting-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span メッセージ送信の基本設定
    .box-body.register-body
      FormInputSendMessageReplyLimit(v-model="replyLimit")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoSendMessage" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="メッセージ送信の基本設定を更新しますか？" @ok="updateMessageSettings")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoSendMessage")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import MessageSettingsService, { MessageSettingsPatchModel } from "@/commons/services/MessageSettings";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormInputSendMessageReplyLimit from "@/pc/components/forms/FormInputSendMessageReplyLimit.vue";

/**
 * ### メッセージ送信基本設定画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    FormInputSendMessageReplyLimit
  }
})
export default class SendMessageSettingEdit extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** メッセージ送信設定サービス */
  @Inject("MessageSettingsService") messageSettingsService!: MessageSettingsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;

  /** メッセージ返信期限 */
  replyLimit: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 使用機能可否は施設の情報なので施設情報を取得する
    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;

    this.replyLimit = facility.messageSettings.replyLimit;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設を取得する
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### メッセージ送信設定を更新する
   */
  public async updateMessageSettings(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: MessageSettingsPatchModel = this.createRequestBody(this.facility as FacilityGetModel);
      await this.messageSettingsService.patch(userStore, requestBody);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 更新パラメータ作成
   */
  public createRequestBody(facility: FacilityGetModel): MessageSettingsPatchModel {
    const params: MessageSettingsPatchModel = {
      messageSettings: {
        replyLimit: this.replyLimit
      },
      updated: facility.updated
    };

    return params;
  }

  /**
   * ### メッセージ送信画面に遷移する
   */
  public gotoSendMessage(): void {
    this.$router.push(PcUrl.WaiterSendMessage);
    return;
  }
}
</script>

<style lang="scss" scoped>
#send-message-setting-edit {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
